import { getValueFromCookies } from "@util/webstorage";
import { windowRef } from "@util/window";
import router from "next/router";
const sanitizePathSegment = (segment: string) => {
    if (!segment) return segment;
    try {
        decodeURIComponent(segment);
        return segment;
    } catch {
        return encodeURIComponent(segment);
    }
};

/** Make a full path/href safe for Next.js route matching and Link prefetch. */
export function sanitizeRoutePath(path: any): string {
    const raw = `${path ?? ""}`;
    if (!raw) return raw;

    const hashIndex = raw.indexOf("#");
    const queryIndex = raw.indexOf("?");
    let cut = raw.length;
    if (queryIndex >= 0) cut = Math.min(cut, queryIndex);
    if (hashIndex >= 0) cut = Math.min(cut, hashIndex);

    const pathname = raw.slice(0, cut);
    const suffix = raw.slice(cut);
    const sanitizedPathname = pathname
        .split("/")
        .map(sanitizePathSegment)
        .join("/");

    return `${sanitizedPathname}${suffix}`;
}

const normalizeBaseRouteUrl = (baseRouteUrl: any) => {
    if (typeof baseRouteUrl !== "string") return "";
    const trimmedBaseRouteUrl = baseRouteUrl.trim();
    if (!trimmedBaseRouteUrl || trimmedBaseRouteUrl === "null" || trimmedBaseRouteUrl === "undefined") return "";
    const sanitizedBaseRouteUrl = trimmedBaseRouteUrl.replace(/\/+$/, "");
    return sanitizedBaseRouteUrl ? `${sanitizedBaseRouteUrl}/` : "/";
}

const getBaseRouteUrlFromCurrentPath = () => {
    const pathname = windowRef()?.location?.pathname || "";
    const [tenantSegment = "", storeSegment = ""] = pathname.split("/").filter(Boolean);
    if (!tenantSegment || !storeSegment || tenantSegment.startsWith("[") || storeSegment.startsWith("[")) {
        return "";
    }
    return `/${tenantSegment}/${storeSegment}/`;
}

export const resolveBaseRouteUrl = (baseRouteUrl = "") => {
    return normalizeBaseRouteUrl(baseRouteUrl)
        || normalizeBaseRouteUrl(getValueFromCookies('baseRouteUrl'))
        || normalizeBaseRouteUrl(getBaseRouteUrlFromCurrentPath())
        || "/";
}

const resolveNavigationPath = (path: any, baseRouteUrl: string) => {
    const sanitizedPath = `${path ?? ""}`.trim();
    if (!sanitizedPath || sanitizedPath === "null" || sanitizedPath === "undefined") {
        return baseRouteUrl;
    }
    if (sanitizedPath.startsWith("/")) {
        return sanitizeRoutePath(sanitizedPath);
    }
    return sanitizeRoutePath(`${baseRouteUrl}${sanitizedPath.replace(/^\/+/, "")}`);
}

export function navigateTo(path: any, baseRouteUrl = "",) {
    const resolvedBaseRouteUrl = resolveBaseRouteUrl(baseRouteUrl);
    router.push(resolveNavigationPath(path, resolvedBaseRouteUrl), undefined, { scroll: false, shallow: true })
}

export function navigateToBack(baseRouteUrl = "") {
    const resolvedBaseRouteUrl = resolveBaseRouteUrl(baseRouteUrl);
    if (windowRef()?.history?.length <= 2) router.push(resolvedBaseRouteUrl, undefined, { scroll: false, shallow: true })
    else router.back();
}

export const redirectToHome = () => {
    navigateTo('home');
}
